import React, { useState, useCallback, useEffect } from 'react';
import {
  Link2, Building2, Globe,
  Plus, Zap, CheckCircle, Trash2, Eye, EyeOff, Pencil,
  AlertCircle, Search, X, Loader, Download, Copy, Check, RefreshCw
} from 'lucide-react';
import Header from '../../components/layout/Header';
import { Modal, Button } from '../../components/common/Common';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { downloadIntegrationDoc, buildSampleCurl } from './integrationDoc';
import './Integration.css';

const getProxyBase = () => {
  const base = (typeof window !== 'undefined' && window.APP_CONFIG?.apiBaseUrl) || '';
  const trimmed = base.endsWith('/') ? base.slice(0, -1) : base;
  return `${trimmed}/lead-integration`;
};

const ALL_SOURCES = [
  { id: '99acres', source_name: '99acres', name: '99acres', description: 'Import property inquiries from 99acres', icon: Building2, color: '#FF6B35', category: 'portal', available: true,
    fields: [
      { key: 'branch', label: 'Branch', placeholder: 'e.g., Main', required: true, group: 'meta' },
      { key: 'acres_username', label: 'Username / Email', placeholder: 'Your 99acres username', required: true, group: 'credentials' },
      { key: 'acres_password', label: 'Password', placeholder: 'Your 99acres password', secure: true, required: true, group: 'credentials' },
    ],
  },
  { id: 'magicbricks', source_name: 'magicbricks', name: 'MagicBricks', description: 'Import leads from MagicBricks portal', icon: Building2, color: '#E53935', category: 'portal', available: true,
    autoKey: true,
    defaults: { mb_mode: 'PUSH' },
    fields: [
      { key: 'branch', label: 'Branch', placeholder: 'e.g., Main', required: true, group: 'meta' },
      { key: 'api_key', label: 'API Key', required: true, generate: true, group: 'credentials' },
    ],
  },
  { id: 'housing', source_name: 'housing', name: 'Housing.com', description: 'Sync leads from Housing.com', icon: Building2, color: '#00C853', category: 'portal', available: true,
    fields: [
      { key: 'branch', label: 'Branch', placeholder: 'e.g., Main', required: true, group: 'meta' },
      { key: 'housing_id', label: 'Housing ID', placeholder: 'Your Housing.com ID', required: true, group: 'credentials' },
      { key: 'housing_key', label: 'Housing Key', placeholder: 'Your Housing.com Key', secure: true, required: true, group: 'credentials' },
    ],
  },
  { id: 'website', source_name: 'website', name: 'Website / WordPress', description: 'Capture leads from your website forms', icon: Globe, color: '#2196F3', category: 'web', available: true,
    autoKey: true,
    fields: [
      { key: 'branch', label: 'Branch', placeholder: 'e.g., Main', required: true, group: 'meta' },
      { key: 'website_name', label: 'Website Name', placeholder: 'e.g., yoursite.com', group: 'credentials' },
      { key: 'api_key', label: 'API Key', required: true, generate: true, group: 'credentials' },
    ],
  },
  { id: 'facebook', source_name: 'facebook', name: 'Facebook Leads', description: 'Auto-import leads from Facebook Lead Ads', icon: Globe, color: '#1877F2', category: 'social', available: false,
    fields: [],
  },
];

const KEY_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
const generateApiKey = () => {
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, b => KEY_CHARS[b % KEY_CHARS.length]).join('');
};

const findSourceMeta = (source_name) =>
  ALL_SOURCES.find(s => s.source_name?.toLowerCase() === String(source_name).toLowerCase());

export default function Integration() {
  const { showToast } = useToast();
  const { clientData } = useAuth();
  // No fallback on purpose: a wrong client code would send this CRM's leads
  // into another client's database
  const clientCode = clientData?.clientCode || '';

  const [connected, setConnected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editInitial, setEditInitial] = useState({});
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [disconnectTarget, setDisconnectTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [webhookInfo, setWebhookInfo] = useState(null);

  const loadConnections = useCallback(async () => {
    if (!clientCode) return;
    setLoading(true);
    try {
      const r = await fetch(`${getProxyBase()}/credentials/${encodeURIComponent(clientCode)}`);
      const data = await r.json();
      const list = Array.isArray(data?.sources) ? data.sources : [];
      const mapped = list.map(item => {
        const meta = findSourceMeta(item.source_name) || {};
        return {
          id: item.credential_id,
          credentialId: item.credential_id,
          sourceId: meta.id || item.source_name,
          source_name: item.source_name,
          name: meta.name || item.source_name,
          sourceName: `${meta.name || item.source_name} — ${item.branch}`,
          branch: item.branch,
          color: meta.color || '#6b7280',
          icon: meta.icon || Globe,
          leadsToday: 0,
          leadsTotal: 0,
          lastSync: '—',
          clientCode,
          webhookUrl: item.webhook_url || '',
          config: { branch: item.branch, ...(item.credentials || {}) },
        };
      });
      setConnected(mapped);
    } catch (err) {
      showToast('Failed to load integrations', 'error');
    } finally {
      setLoading(false);
    }
  }, [clientCode, showToast]);

  useEffect(() => { loadConnections(); }, [loadConnections]);

  const filteredSources = ALL_SOURCES.filter(s => {
    if (search && !s.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const handleConnect = useCallback((source) => {
    if (!clientCode) {
      showToast('Client code not available — sign in again before connecting a source', 'error');
      return;
    }
    setSelectedSource(source);
    setShowConnectModal(true);
  }, [clientCode, showToast]);

  const handleSubmitConnect = useCallback(async (source, formData) => {
    if (!clientCode) {
      showToast('Client code not available', 'error');
      return;
    }
    const credentials = {};
    let branch = '';
    (source.fields || []).forEach(f => {
      if (f.group === 'meta' && f.key === 'branch') branch = formData[f.key];
      else if (f.group === 'credentials') credentials[f.key] = formData[f.key];
    });
    // Source-level constants the user never fills in (e.g. MagicBricks mb_mode)
    Object.entries(source.defaults || {}).forEach(([k, v]) => {
      if (credentials[k] === undefined || credentials[k] === '') credentials[k] = v;
    });

    const isEdit = editMode;
    setSubmitting(true);
    try {
      const r = await fetch(`${getProxyBase()}/credentials`, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_code: clientCode,
          source_name: source.source_name,
          branch,
          credentials,
        }),
      });
      const data = await r.json();
      if (!r.ok || data?.success === false) {
        if (r.status === 409) {
          showToast(`${source.name} is already connected for branch "${branch}" — edit that connection or use a different branch`, 'error');
          return;
        }
        showToast(data?.error || data?.message || (isEdit ? 'Failed to update' : 'Failed to connect'), 'error');
        return;
      }
      showToast(isEdit ? `${source.name} updated successfully!` : `${source.name} connected successfully!`, 'success');
      setShowConnectModal(false);
      setSelectedSource(null);
      setEditMode(false);
      setEditInitial({});
      // Webhook sources get their key minted server-side — surface it right away
      if (source.autoKey && data?.webhook_url) {
        setWebhookInfo({
          sourceId: source.id,
          sourceName: source.name,
          clientCode,
          branch,
          apiKey: data.api_key || credentials.api_key,
          webhookUrl: data.webhook_url,
          websiteName: credentials.website_name || '',
        });
      }
      loadConnections();
    } catch (err) {
      showToast('Connection failed', 'error');
    } finally {
      setSubmitting(false);
    }
  }, [clientCode, showToast, loadConnections, editMode]);

  const handleEdit = useCallback((intg) => {
    const sourceMeta = ALL_SOURCES.find(
      s => s.source_name?.toLowerCase() === String(intg.source_name).toLowerCase()
    );
    if (!sourceMeta) {
      showToast(`Source "${intg.source_name}" is not editable`, 'error');
      return;
    }
    setSelectedSource(sourceMeta);
    setEditInitial({ branch: intg.branch, ...(intg.config || {}) });
    setEditMode(true);
    setShowDetailsModal(false);
    setShowConnectModal(true);
  }, [showToast]);

  const confirmDisconnect = useCallback(async () => {
    if (!disconnectTarget || !clientCode) return;
    setSubmitting(true);
    try {
      const r = await fetch(`${getProxyBase()}/credentials`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_code: clientCode,
          source_name: disconnectTarget.source_name,
          branch: disconnectTarget.branch,
        }),
      });
      const data = await r.json();
      if (!r.ok || data?.success === false) {
        showToast(data?.error || data?.message || 'Failed to disconnect', 'error');
        return;
      }
      showToast(`${disconnectTarget.name} disconnected`, 'success');
      setShowDisconnectConfirm(false);
      setShowDetailsModal(false);
      setDisconnectTarget(null);
      loadConnections();
    } catch (err) {
      showToast('Disconnect failed', 'error');
    } finally {
      setSubmitting(false);
    }
  }, [disconnectTarget, clientCode, showToast, loadConnections]);

  return (
    <div>
      <Header
        title="API Integrations"
        subtitle="Connect third-party platforms to auto-import leads"
      />
      <div className="page animate-fade-in">
        {!clientCode && (
          <div className="intg-alert">
            <AlertCircle size={16} style={{ flexShrink: 0 }} />
            <span>Client code not available. Sign in again — integrations can't be loaded or connected without it.</span>
          </div>
        )}

        {/* Hero */}
        <div className="intg-hero">
          <div className="intg-hero__icon"><Link2 size={26} /></div>
          <div className="intg-hero__content">
            <h2 className="intg-hero__title">Lead Source Integrations</h2>
            <p className="intg-hero__sub">Connect third-party portals to auto-import leads into your pipeline.</p>
            <div className="intg-hero__chips">
              <span className="intg-hero__chip">
                <Link2 size={13} /> <strong>{connected.length}</strong> Connected
              </span>
            </div>
          </div>
        </div>

        {/* Connected */}
        {loading ? (
          <div className="intg-loading animate-fade-in">
            <Loader size={28} className="intg-spin" />
            <p>Loading integrations...</p>
          </div>
        ) : connected.length > 0 && (
          <div className="intg-section-card">
            <div className="intg-section__head">
              <h2 className="intg-section__title"><CheckCircle size={13} /> Active Connections · {connected.length}</h2>
            </div>
            <div className="intg-connected">
              {connected.map(intg => {
                const IC = intg.icon;
                return (
                  <div key={intg.id} className="intg-card">
                    <div className="intg-card__icon">
                      <IC size={20} />
                    </div>
                    <div className="intg-card__info">
                      <div className="intg-card__name-row">
                        <span className="intg-card__name">{intg.name}</span>
                        <span className="intg-card__on">On</span>
                      </div>
                      <span className="intg-card__branch">{intg.branch}</span>
                    </div>
                    <div className="intg-card__actions">
                      <button
                        className="intg-card__icon-btn"
                        title="View"
                        onClick={() => { setSelectedIntegration(intg); setShowDetailsModal(true); }}
                      >
                        <Eye size={15} />
                      </button>
                      <button
                        className="intg-card__icon-btn"
                        title="Edit"
                        onClick={() => handleEdit(intg)}
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        className="intg-card__icon-btn intg-card__icon-btn--danger"
                        title="Disconnect"
                        onClick={() => { setDisconnectTarget(intg); setShowDisconnectConfirm(true); }}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Available Sources */}
        <div className="intg-section-card">
          <div className="intg-section__head">
            <h2 className="intg-section__title"><Plus size={13} /> Available Integrations</h2>
          </div>
          <div className="intg-filters">
            <div className="intg-search">
              <Search size={16} />
              <input placeholder="Search integrations..." value={search} onChange={(e) => setSearch(e.target.value)} />
              {search && <button onClick={() => setSearch('')}><X size={14} /></button>}
            </div>
          </div>
          <div className="intg-sources">
            {filteredSources.map(source => {
              const IC = source.icon;
              return (
                <div key={source.id}
                  className={`intg-source ${!source.available ? 'intg-source--disabled' : ''}`}
                  onClick={() => source.available && handleConnect(source)}>
                  <div className="intg-source__top">
                    <div className="intg-source__icon">
                      <IC size={22} />
                    </div>
                    <div className="intg-source__info">
                      <span className="intg-source__name">{source.name}</span>
                      <span className="intg-source__desc">{source.description}</span>
                    </div>
                  </div>
                  {source.available ? (
                    <button className="intg-source__btn" type="button">
                      <Plus size={13} /> Connect
                    </button>
                  ) : (
                    <span className="intg-source__soon">Coming Soon</span>
                  )}
                </div>
              );
            })}
            {filteredSources.length === 0 && (
              <div className="intg-empty"><Search size={36} /><p>No integrations found</p></div>
            )}
          </div>
        </div>
      </div>

      {/* Connect / Edit Form Modal */}
      <ConnectFormModal isOpen={showConnectModal} source={selectedSource} submitting={submitting}
        editMode={editMode} initialData={editInitial}
        onClose={() => {
          setShowConnectModal(false); setSelectedSource(null);
          setEditMode(false); setEditInitial({});
        }}
        onSubmit={handleSubmitConnect} />

      {/* Webhook Ready (auto-generated key + endpoint) */}
      <WebhookInfoModal info={webhookInfo} onClose={() => setWebhookInfo(null)} />

      {/* Details Modal */}
      <DetailsModal isOpen={showDetailsModal} integration={selectedIntegration}
        onClose={() => { setShowDetailsModal(false); setSelectedIntegration(null); }}
        onEdit={handleEdit}
        onDisconnect={(intg) => { setDisconnectTarget(intg); setShowDisconnectConfirm(true); }} />

      {/* Disconnect Confirm */}
      <Modal isOpen={showDisconnectConfirm} onClose={() => setShowDisconnectConfirm(false)} title="Disconnect Integration" size="sm">
        <div className="intg-disconnect">
          <AlertCircle size={40} className="intg-disconnect__icon" />
          <p>Are you sure you want to disconnect <strong>{disconnectTarget?.name}</strong> ({disconnectTarget?.branch})?</p>
          <p className="intg-disconnect__sub">Lead syncing will stop immediately. Existing leads won't be affected.</p>
          <div className="intg-disconnect__actions">
            <Button variant="ghost" onClick={() => setShowDisconnectConfirm(false)} disabled={submitting}>Cancel</Button>
            <Button variant="danger" onClick={confirmDisconnect} disabled={submitting}>
              {submitting ? 'Disconnecting...' : 'Disconnect'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

/* ── Copyable read-only field ── */
function CopyField({ label, value, mono = true }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard?.writeText(value || '').then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  };

  return (
    <div className="intg-wh__field">
      <span className="intg-wh__label">{label}</span>
      <div className="intg-wh__row">
        <code className={`intg-wh__value ${mono ? 'intg-wh__value--mono' : ''}`}>{value}</code>
        <button type="button" className="intg-wh__copy" onClick={copy} title={copied ? 'Copied' : 'Copy'}>
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
      </div>
    </div>
  );
}

/* ── Webhook Ready Modal (endpoint + generated key + doc) ── */
function WebhookInfoModal({ info, onClose }) {
  if (!info) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="intg-det-modal animate-scale-in" onClick={e => e.stopPropagation()}>
        <div className="intg-det__header">
          <div className="intg-det__header-left">
            <div>
              <h3 className="intg-det__name">{info.sourceName} is ready</h3>
              <p className="intg-det__source">Share these details with {info.sourceName} to start receiving leads</p>
            </div>
          </div>
          <button className="intg-det__close-x" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="intg-det__body">
          <div className="intg-form__banner">
            <AlertCircle size={15} style={{ flexShrink: 0 }} />
            <span>The API key was generated for you. Keep it private — anyone with it can post leads.</span>
          </div>

          <div className="intg-wh__grid">
            <CopyField label="Endpoint (POST)" value={info.webhookUrl} />
            <CopyField label="API Key (X-API-Key header)" value={info.apiKey} />
            <CopyField label="Sample cURL" value={buildSampleCurl(info)} />
          </div>
        </div>

        <div className="intg-det__actions">
          <button className="intg-det__act intg-det__act--close" onClick={onClose}>Close</button>
          <button className="intg-det__act intg-det__act--primary" onClick={() => downloadIntegrationDoc(info)}>
            <Download size={15} /> Download PDF
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Connect / Edit Form Modal ── */
function ConnectFormModal({ isOpen, source, submitting, editMode, initialData, onClose, onSubmit }) {
  const [formData, setFormData] = useState({});
  const [showSecure, setShowSecure] = useState({});
  const [errors, setErrors] = useState({});
  const { showToast } = useToast();

  React.useEffect(() => {
    if (source) {
      const data = editMode && initialData ? { ...initialData } : {};
      // Webhook sources: start with a key ready, user can re-roll before saving
      (source.fields || []).forEach(f => {
        if (f.generate && !data[f.key]) data[f.key] = generateApiKey();
      });
      setFormData(data);
      setShowSecure({});
      setErrors({});
    }
  }, [source, editMode, initialData]);

  if (!isOpen || !source) return null;
  const IC = source.icon;

  const handleChange = (key, val) => {
    setFormData(p => ({ ...p, [key]: val }));
    if (errors[key]) setErrors(p => ({ ...p, [key]: null }));
  };

  const handleSubmit = () => {
    const errs = {};
    (source.fields || []).forEach(f => {
      if (f.required && (!formData[f.key] || String(formData[f.key]).trim() === ''))
        errs[f.key] = `${f.label} is required`;
    });
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      showToast(Object.values(errs)[0], 'error');
      return;
    }
    onSubmit(source, formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="intg-form-modal animate-scale-in" onClick={e => e.stopPropagation()}>
        <div className="intg-form__header">
          <div className="intg-form__header-left">
            <div className="intg-form__icon">
              <IC size={22} />
            </div>
            <div>
              <h3 className="intg-form__title">{editMode ? 'Edit' : 'Connect'} {source.name}</h3>
              <p className="intg-form__sub">{editMode ? 'Update your account details' : 'Enter your account details'}</p>
            </div>
          </div>
          <button className="modal__close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="intg-form__banner">
          <AlertCircle size={15} style={{ flexShrink: 0 }} />
          <span>
            {source.autoKey
              ? `Generate an API key below, then share the setup PDF with ${source.name}`
              : `Get these details from your ${source.name} dashboard`}
          </span>
        </div>

        <div className="intg-form__body">
          {(source.fields || []).map(field => (
            <div key={field.key} className="intg-form__field">
              <label className="intg-form__label">
                {field.label} {field.required && <span className="intg-form__req">*</span>}
              </label>
              <div className={`intg-form__input-wrap ${errors[field.key] ? 'intg-form__input-wrap--error' : ''}`}>
                <input
                  type={field.secure && !showSecure[field.key] ? 'password' : 'text'}
                  className={`intg-form__input ${field.generate ? 'intg-form__input--mono' : ''}`}
                  placeholder={field.placeholder}
                  value={formData[field.key] || ''}
                  readOnly={!!field.generate}
                  onChange={e => handleChange(field.key, e.target.value)}
                />
                {field.generate && (
                  <button className="intg-form__gen" type="button"
                    title="Generate a new key"
                    onClick={() => handleChange(field.key, generateApiKey())}>
                    <RefreshCw size={13} /> Generate
                  </button>
                )}
                {field.secure && (
                  <button className="intg-form__eye" type="button"
                    onClick={() => setShowSecure(p => ({ ...p, [field.key]: !p[field.key] }))}>
                    {showSecure[field.key] ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                )}
              </div>
              {errors[field.key] && <span className="intg-form__error">{errors[field.key]}</span>}
            </div>
          ))}
        </div>

        <div className="intg-form__actions">
          <button className="intg-form__cancel" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="intg-form__submit" onClick={handleSubmit} disabled={submitting}>
            {editMode
              ? <><Pencil size={15} /> {submitting ? 'Saving...' : 'Save Changes'}</>
              : <><Zap size={15} /> {submitting ? 'Connecting...' : 'Connect'}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Details Modal ── */
const isSensitiveKey = (k) => {
  const s = String(k || '').toLowerCase();
  return s.includes('password') || s.includes('token') || s.includes('secret') ||
         (s.includes('key') && !s.includes('keyword'));
};
const formatLabel = (k) =>
  String(k || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

function DetailsModal({ isOpen, integration, onClose, onEdit, onDisconnect }) {
  const [revealed, setRevealed] = React.useState({});
  React.useEffect(() => { setRevealed({}); }, [integration]);
  if (!isOpen || !integration) return null;

  const config = integration.config || {};
  const branch = config.branch;
  const credEntries = Object.entries(config).filter(([k]) => k !== 'branch');
  const toggle = (k) => setRevealed(p => ({ ...p, [k]: !p[k] }));
  const docInfo = integration.webhookUrl ? {
    sourceId: integration.sourceId,
    sourceName: integration.name,
    clientCode: integration.clientCode,
    branch: integration.branch,
    apiKey: config.api_key || '',
    webhookUrl: integration.webhookUrl,
    websiteName: config.website_name || '',
  } : null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="intg-det-modal animate-scale-in" onClick={e => e.stopPropagation()}>
        <div className="intg-det__header">
          <div className="intg-det__header-left">
            <div>
              <h3 className="intg-det__name">{integration.name} Credential</h3>
              <p className="intg-det__source">{integration.source_name} · {integration.branch}</p>
            </div>
          </div>
          <button className="intg-det__close-x" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="intg-det__body">
          <div className="intg-det__section">
            <div className="intg-det__section-bar">
              <span className="intg-det__section-title">Source Information</span>
            </div>
            <div className="intg-det__section-body">
              <div className="intg-det__field-grid">
                <div className="intg-det__field">
                  <span className="intg-det__field-label">Source</span>
                  <span className="intg-det__field-value">{integration.name}</span>
                </div>
                {branch !== undefined && (
                  <div className="intg-det__field">
                    <span className="intg-det__field-label">Branch</span>
                    <span className="intg-det__field-value">{branch}</span>
                  </div>
                )}
                <div className="intg-det__field">
                  <span className="intg-det__field-label">Status</span>
                  <span className="intg-det__field-value intg-det__field-value--success">Active</span>
                </div>
              </div>
            </div>
          </div>

          {docInfo && (
            <div className="intg-det__section">
              <div className="intg-det__section-bar">
                <span className="intg-det__section-title">Webhook</span>
              </div>
              <div className="intg-det__section-body">
                <div className="intg-wh__grid">
                  <CopyField label="Endpoint (POST)" value={docInfo.webhookUrl} />
                  <CopyField label="Sample cURL" value={buildSampleCurl(docInfo)} />
                </div>
              </div>
            </div>
          )}

          {credEntries.length > 0 && (
            <div className="intg-det__section">
              <div className="intg-det__section-bar">
                <span className="intg-det__section-title">Credentials</span>
              </div>
              <div className="intg-det__section-body">
                <div className="intg-det__field-grid">
                  {credEntries.map(([k, v]) => {
                    const sensitive = isSensitiveKey(k);
                    const shown = revealed[k];
                    const display = sensitive && !shown
                      ? '•'.repeat(Math.min(12, String(v).length))
                      : v;
                    return (
                      <div key={k} className="intg-det__field">
                        <span className="intg-det__field-label">{formatLabel(k)}</span>
                        <div className="intg-det__field-row">
                          <span className={`intg-det__field-value ${sensitive ? 'intg-det__field-value--mono' : ''}`}>
                            {display}
                          </span>
                          {sensitive && (
                            <button
                              type="button"
                              className="intg-det__eye"
                              onClick={() => toggle(k)}
                              title={shown ? 'Hide' : 'Show'}
                            >
                              {shown ? <EyeOff size={14} /> : <Eye size={14} />}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="intg-det__actions">
          <button className="intg-det__act intg-det__act--close"
            onClick={onClose}>
            Close
          </button>
          {docInfo && (
            <button className="intg-det__act intg-det__act--primary"
              onClick={() => downloadIntegrationDoc(docInfo)}>
              <Download size={15} /> Download PDF
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
